// Login / logout helpers that drive the auth store.
//
// The built-in login flow has three possible response shapes:
// - 200 with {access_token, expires_in, token_type} → logged in
// - 200 with {totp_required: true, challenge_id} → needs TOTP step
// - 403 (email not verified) / 401 (wrong creds) / 429 (locked)

import { refreshAccessToken } from "@/api/client";

import { endSession } from "./session";
import { useAuth, type AuthUser } from "./stub";
import { errorDetail } from "@/lib/apiError";

interface LoginOk {
  kind: "ok";
}
interface LoginTotp {
  kind: "totp";
  challengeId: string;
}
interface LoginError {
  kind: "error";
  status: number;
  message: string;
  /** The API's error code, when it sent one. */
  code?: string;
}

export type LoginOutcome = LoginOk | LoginTotp | LoginError;

// Takes the token explicitly (rather than going through apiFetch)
// because it is only ever called with a token that was issued a
// moment ago and isn't in the store yet; a 401 here means "no
// profile", not "refresh and retry".
async function _fetchMe(accessToken: string): Promise<AuthUser | null> {
  const res = await fetch("/api/v1/auth/me", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) return null;
  const body = (await res.json()) as AuthUser;
  return body;
}

async function _finalise(accessToken: string): Promise<LoginOutcome> {
  const me = await _fetchMe(accessToken);
  if (!me) {
    return { kind: "error", status: 500, message: "could not load profile" };
  }
  useAuth.getState().setSession(accessToken, me);
  return { kind: "ok" };
}

export async function login(
  email: string,
  password: string,
): Promise<LoginOutcome> {
  const res = await fetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  if (res.status === 200) {
    const body = (await res.json()) as
      | { access_token: string; expires_in: number; token_type: "bearer" }
      | { totp_required: true; challenge_id: string };
    if ("totp_required" in body) {
      return { kind: "totp", challengeId: body.challenge_id };
    }
    return _finalise(body.access_token);
  }
  let detail = `HTTP ${res.status}`;
  try {
    detail = errorDetail(await res.json(), detail);
  } catch {
    // ignore
  }
  return { kind: "error", status: res.status, message: detail };
}

export async function submitTotp(
  challengeId: string,
  code: string,
): Promise<LoginOutcome> {
  const res = await fetch("/api/v1/auth/login/totp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ challenge_id: challengeId, code }),
  });
  if (res.status === 200) {
    const body = (await res.json()) as { access_token: string };
    return _finalise(body.access_token);
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // ignore
  }
  const errorCode =
    body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : undefined;
  return {
    kind: "error",
    status: res.status,
    message: errorDetail(body, `HTTP ${res.status}`),
    code: errorCode,
  };
}

/** True when this browser can use passkeys (secure context only). */
export function passkeysSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential === "function"
  );
}

/**
 * Sign in with a passkey registered to ``email``. No TOTP step: the
 * server requires the device's PIN or biometric, which makes the
 * passkey two factors by itself. ``code`` is ``"cancelled"`` when the
 * person closed the browser's passkey prompt or it timed out.
 */
export async function loginWithPasskey(email: string): Promise<LoginOutcome> {
  const begin = await fetch("/api/v1/auth/webauthn/authenticate/begin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  if (!begin.ok) {
    let body: unknown = null;
    try {
      body = await begin.json();
    } catch {
      // ignore
    }
    return {
      kind: "error",
      status: begin.status,
      message: errorDetail(body, `HTTP ${begin.status}`),
    };
  }
  const { startAuthentication } = await import("@simplewebauthn/browser");
  const { challenge_id, options } = (await begin.json()) as {
    challenge_id: string;
    options: Parameters<typeof startAuthentication>[0]["optionsJSON"];
  };
  let credential;
  try {
    credential = await startAuthentication({ optionsJSON: options });
  } catch (e) {
    return {
      kind: "error",
      status: 0,
      message: e instanceof Error ? e.message : "passkey cancelled",
      code: "cancelled",
    };
  }
  const complete = await fetch("/api/v1/auth/webauthn/authenticate/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ challenge_id, credential }),
  });
  if (complete.status === 200) {
    const body = (await complete.json()) as { access_token: string };
    return _finalise(body.access_token);
  }
  let body: unknown = null;
  try {
    body = await complete.json();
  } catch {
    // ignore
  }
  return {
    kind: "error",
    status: complete.status,
    message: errorDetail(body, `HTTP ${complete.status}`),
  };
}

export async function logout(): Promise<void> {
  let idpLogoutUrl: string | null = null;
  try {
    const res = await fetch("/api/v1/auth/logout", {
      method: "POST",
      credentials: "include",
    });
    if (res.ok) {
      const body = (await res.json()) as { idp_logout_url?: string | null };
      idpLogoutUrl = body.idp_logout_url ?? null;
    }
  } catch {
    // best-effort; clear client state either way
  }
  // Not just the auth store: cached listings and running uploads
  // belong to the user who is leaving.
  endSession();
  // Accounts from a single sign-on provider: end its session too, or the
  // next "Log in with ..." goes through without a password. The provider
  // asks to confirm and comes back to the login page.
  if (idpLogoutUrl) window.location.assign(idpLogoutUrl);
}

let restorePromise: Promise<boolean> | null = null;

async function doRestore(): Promise<boolean> {
  try {
    // Shares the API client's single-flight refresh: a 401 from a
    // query racing this restore must not trigger a second refresh
    // with the same (about to be rotated) cookie.
    const token = await refreshAccessToken();
    if (!token) return false;
    const me = await _fetchMe(token);
    if (!me) return false;
    // A concurrent refresh may have rotated the token again while
    // /auth/me was in flight; keep whatever is newest.
    useAuth.getState().setSession(useAuth.getState().accessToken ?? token, me);
    return true;
  } catch {
    return false;
  } finally {
    useAuth.getState().markSessionChecked();
  }
}

/**
 * Recover a session from the refresh cookie — once per page load.
 *
 * Every caller (the root layout, each AuthGate) gets the same
 * promise, so navigating between routes never re-runs the refresh.
 * Always settles and always marks the session as checked, so the
 * UI can fall back to the login form instead of hanging on a blank
 * screen when the network or the API is down.
 */
export function ensureSessionRestored(): Promise<boolean> {
  if (restorePromise === null) {
    restorePromise = doRestore();
  }
  return restorePromise;
}

/** Forget the memoised restore so the next call runs again. Test-only. */
export function _resetSessionRestoreForTests(): void {
  restorePromise = null;
}
